import React, { useState, useRef } from 'react';
import { X, Upload, FileText } from 'lucide-react';
import { useApp } from '../../context/AppContext';

export const UploadModal: React.FC = () => {
  const { showUploadModal, setShowUploadModal, addDocument } = useApp();
  const [docName, setDocName] = useState('');
  const [templateFile, setTemplateFile] = useState<File | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const templateInputRef = useRef<HTMLInputElement>(null);

  if (!showUploadModal) return null;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) setSelectedFile(file);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || isUploading) return;

    const name = docName.trim() || selectedFile.name.replace(/\.[^.]+$/, '');
    const template = templateFile
      ? templateFile.name.replace(/\.[^.]+$/, '')
      : 'Auto-detect';

    setIsUploading(true);
    try {
      await addDocument(selectedFile, name, template);
      // Context closes the modal and shows toast on success
    } catch {
      // Context/API layer shows error toast
    } finally {
      setIsUploading(false);
      setSelectedFile(null);
      setDocName('');
      setTemplateFile(null);
    }
  };

  const handleClose = () => {
    if (isUploading) return;
    setShowUploadModal(false);
    setSelectedFile(null);
    setDocName('');
    setTemplateFile(null);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-surface-900/30 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative glass-card shadow-2xl w-full max-w-lg p-6 animate-fade-in">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-surface-900">Upload Document for TRACE AI Analysis</h2>
          <button onClick={handleClose} className="text-surface-400 hover:text-surface-600 transition-colors">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* File Drop Zone */}
          <div
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            className={isDragging ? 'upload-zone-active' : 'upload-zone'}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.tiff,.tif,.bmp"
              onChange={handleFileSelect}
              className="hidden"
            />
            {selectedFile ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center">
                  <FileText size={20} className="text-brand-400" />
                </div>
                <div>
                  <p className="text-sm font-medium text-surface-800">{selectedFile.name}</p>
                  <p className="text-xs text-surface-400">{formatFileSize(selectedFile.size)}</p>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setSelectedFile(null); }}
                  className="ml-auto text-surface-400 hover:text-surface-600"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <div className="text-center">
                <Upload size={32} className="mx-auto mb-2 text-surface-400" />
                <p className="text-sm font-medium text-surface-600">Drop your document here</p>
                <p className="text-xs text-surface-400 mt-1">or click to browse (PDF, DOC, DOCX, PNG, JPG, TIFF, BMP)</p>
              </div>
            )}
          </div>

          {/* Document Name */}
          <div>
            <label className="text-[11px] font-semibold text-surface-500 uppercase tracking-wider">Document Name</label>
            <input
              type="text"
              value={docName}
              onChange={(e) => setDocName(e.target.value)}
              placeholder="e.g. Compliance Report v1.0"
              className="input-field mt-1"
            />
          </div>

          {/* Template (Optional) */}
          <div>
            <label className="text-[11px] font-semibold text-surface-500 uppercase tracking-wider">Compliance Template</label>
            <p className="text-[10px] text-surface-400 mt-0.5 mb-1.5">
              Optionally upload a template file (defaults to auto-detect)
            </p>
            {templateFile ? (
              <div className="flex items-center gap-3 p-2.5 bg-surface-50 border border-surface-200 rounded-xl">
                <FileText size={16} className="text-brand-400 flex-shrink-0" />
                <span className="text-xs font-medium text-surface-700 truncate flex-1">{templateFile.name}</span>
                <button
                  type="button"
                  onClick={() => setTemplateFile(null)}
                  className="text-surface-400 hover:text-surface-600 transition-colors flex-shrink-0"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => templateInputRef.current?.click()}
                className="w-full p-2.5 border border-dashed border-surface-300 rounded-xl text-xs text-surface-400
                  hover:border-brand-400 hover:text-brand-400 transition-colors text-center"
              >
                Click to upload template
              </button>
            )}
            <input
              ref={templateInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.tiff,.tif,.bmp"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) setTemplateFile(f); }}
              className="hidden"
            />
          </div>

          {/* Buttons */}
          <div className="flex items-center gap-3 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={isUploading}
              className="btn-secondary flex-1 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!selectedFile || isUploading}
              className="btn-primary flex-1 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isUploading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" />
                    <path d="M12 2a10 10 0 019.95 9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" />
                  </svg>
                  Uploading…
                </>
              ) : (
                'Upload Document'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Keep backward compat export
export const NewTraceModal = UploadModal;
